define((require) => {
    "use strict";

    const {
        clone, each, isArray, object
    } = require("underscore");
    const Preconditions                  = require("shared/util/Preconditions");

    function curry(fn, ...args) {
        return (...immediateArgs) => fn(...args.concat(immediateArgs));
    }

    /**
     * Removes falsy values from the provided object.
     * @param {Object} obj - Object from which falsy attributes will be removed.
     * @returns {Object} - Object without falsy attributes.
     */
    function compactObject(obj) {
        // Underscore < 1.7 required
        // return _.pick(obj, _.identity);
        const cloned = clone(obj);

        each(cloned, (v, k) => {
            if (!v) {
                delete cloned[k];
            }
        });

        return cloned;
    }

    function autoCurry(fn, numArgs = fn.length) {
        return (...args) => {
            if (args.length < numArgs) {
                const rem = numArgs - args.length;

                if (numArgs - rem > 0) {
                    return autoCurry(curry(...[fn].concat(args)), rem);
                }

                return curry(...[fn].concat(args));
            }

            return fn(...args);
        };
    }

    /**
     * Returns an array of n number of x's.
     *
     * Example.
     *  repeat(3, "foo");   // ["foo", "foo", "foo"]
     *
     * @param {Number} n    - The number of x's you want in the array.
     * @param {Any} x       - The content of the array.
     * @return {Array}      - Array of x's
     */
    const repeat = autoCurry((n, x) => {
        const result = [];

        while (n--) {
            result.push(x);
        }

        return result;
    });

    /**
     * General utility functions.
     *
     * @class GeneralFunctions
     */
    return {

        /**
         * Example:
         *  _.map([1,2,3,4], eq(3))     => [false, false, true, false]
         */
        eq: autoCurry((a, b) => a === b),

        neq: autoCurry((a, b) => a !== b),

        add: autoCurry((a, b) => b + a),

        subtract: autoCurry((a, b) => b - a),

        multiply: autoCurry((a, b) => b * a),

        divide: autoCurry((a, b) => b / a),

        /**
         * Example:
         *  var listOfObj = [ ...list of objects with a name inside each... ];
         *  var names = _.map(listOfObj, get("name"));
         */
        get: autoCurry((prop, obj) => obj[prop]),

        /**
         * Sorts an array of numbers (Just calling .sort() will sort 100
         * before 2, this won't).
         */
        sortNumbers(list) {
            list.sort((a, b) => a - b);
            return list;
        },

        /**
         * Takes a sorted array and a value and returns the number from the array that is
         * closest to the provided value.
         *
         * Example:
         *  getClosestValue([1,4,7,8], 5)   => 4
         *
         */
        getClosestValue: autoCurry((arr, x) => {
            let lo = -1;
            let hi = arr.length;

            while (hi - lo > 1) {
                const mid = Math.round((lo + hi) / 2);

                if (arr[mid] <= x) {
                    lo = mid;
                } else {
                    hi = mid;
                }
            }

            if (arr[lo] === x) {
                hi = lo;
            }

            // Edge cases.
            if (arr[lo] === undefined) {
                return arr[hi];
            }

            if (arr[hi] === undefined) {
                return arr[lo];
            }

            return Math.abs(arr[lo] - x) < Math.abs(arr[hi] - x) ? arr[lo] : arr[hi];
        }),

        /**
         * Curried implementation of map.
         *
         * Example:
         *  var alertAll = map(alert);
         *  alertAll([1,2,3]);      => alerts 1.. 2.. 3...
         */
        map: autoCurry((fn, coll) => coll.map(fn)),

        /**
         * You give it a function and an object and it returns a new object with the same keys,
         * and the functions having been run on each value
         *
         * Example:
         *      mapOverObj(function(n){return n+1}, {one:1, two:2})     => {one:2, two: 3}
         *
         */
        mapOverObj: autoCurry((fn, obj) => {
            const result = {};
            Preconditions.isFunction(fn);

            for (const key in obj) {
                if (obj.hasOwnProperty(key)) {
                    result[key] = fn(obj[key]);
                }
            }

            return result;
        }),

        repeat,

        // This is different from the underscore version, as it will cut off the longest array
        // to fit the size of the shorter.
        zip: autoCurry((arr, arr2) => {
            const result = [];
            const len = Math.min(arr.length, arr2.length);
            let i = -1;

            while (++i < len) {
                result.push([arr[i], arr2[i]]);
            }

            return result;
        }),

        /**
         * Takes two arrays, one of keys, and one of values. The items in the
         * keys array are expected to be unique. It will return an object with
         * the items from the keys aray as keys, and the items in the values
         * array as the matching values.
         *
         * Alternatively, the "values" array can be a single value, which will
         * then be repeated as the value for each key.
         *
         * Example.
         *  arraysToObj(["one", "two"], [1, 2])     => {one: 1, two: 2}
         *  arraysToObj(["one", "two"], "foo")      => {one: "foo", two: "foo"}
         *
         * @param {Array} keys      Array of values that will be used as keys.
         * @param {Array|*} values  Array of values, or single value.
         * @returns {Object}        See example above.
         */
        arraysToObj: autoCurry((keys, values) => {
            if (!isArray(values)) {
                values = repeat(keys.length, values);
            }

            return object(keys, values);
        }),

        /**
         * Call curry on any function and it will be a curried version of it.
         *
         * Examples:
         *      var add = function (x, y) { return x+y }
         *      add(5, 2)                       => 7
         *      add(5)(2)                       => NaN  (Not what you want)
         *      var curriedAdd = curry(add);
         *      curriedAdd(5, 2)                => 7
         *      curriedAdd(5)(2)                => 7
         *
         *      var add = curry(function (x, y) { return x+y });
         *      _.map([1, 2, 3], add(2))        => [3, 5, 6]
         */
        curry: autoCurry,

        compactObject
    };
});



// WEBPACK FOOTER //
// ./src/editor/util/GeneralFunctions.js